import { memo, useCallback } from 'react';

import { type CustomField, type CustomFieldTypeName } from '@air/api/types';
import { ConfirmationModal } from '@air/component-confirmation-modal';

import { useRestoreCustomField } from '~/components/RecentlyDeleted/hooks/useRestoreCustomField';

export type RecentlyDeletedRecoverCustomFieldModalProps = {
  id: CustomField['id'];
  name: CustomField['name'];
  fieldType: CustomFieldTypeName;
};

export const RecentlyDeletedRecoverCustomFieldModal = memo(
  ({ id, name, fieldType, onClose }: AirModalProps<RecentlyDeletedRecoverCustomFieldModalProps>) => {
    const { restoreCustomField } = useRestoreCustomField();

    const handleConfirm = useCallback(() => {
      restoreCustomField.mutate(
        {
          customFieldId: id,
          customFieldName: name,
          customFieldType: fieldType,
        },
        {
          onSuccess: onClose,
        },
      );
    }, [fieldType, id, name, onClose, restoreCustomField]);

    return (
      <ConfirmationModal
        ctaPrimaryText="Recover"
        isOpen
        onDismiss={onClose}
        onConfirm={handleConfirm}
        description={`Recovering "${name}" will restore this custom field and all its values on your assets and boards.`}
        id="recently-deleted-recover-custom-field"
        title="Recover custom field?"
        isLoading={restoreCustomField.isPending}
        isDisabled={restoreCustomField.isPending}
      />
    );
  },
);

RecentlyDeletedRecoverCustomFieldModal.displayName = 'RecentlyDeletedRecoverCustomFieldModal';
